import React, { useContext } from 'react';
import CompanySectionLayout from '../../../../component/B2B/CompanySectionLayout';
import { SidebarContext } from '../../../../context/SidebarContext';
import { getLocalStorageItem } from '../../../../utils/helper';
import { PLAN_PATHS } from './guidedPathTheme';

/**
 * Company panel → Guided Path.
 *
 *   Overview     take-up across both kinds, and whether the work gets done
 *   AI Plan      the plan generated from a prompt the person wrote
 *   Custom Plan  the plan built from the onboarding questionnaire
 *   Employees    who is using it and how much
 *
 * A tab per kind rather than one merged page, because the two are created
 * differently, expire differently and are treated differently by the job that
 * marks plans complete — and because "which of the two is actually landing" is
 * the question a benefits team asks first.
 *
 * NOTHING ANYBODY WROTE APPEARS ANYWHERE IN THIS SECTION. A plan's name is the
 * first fifteen characters of the prompt they typed; its rituals were written
 * for them personally; the questionnaire behind a custom plan is up to five
 * hundred characters on what they are struggling with and why. The pages behind
 * this layout read an id, a flag and a date per document and nothing else.
 *
 * The Employees tab is engagement only, and there is no page behind a row — the
 * only thing a detail view could add is the content itself.
 */
const CompanyGuidedPathLayout = ({ children }) => {
  const { isB2BFansMode } = useContext(SidebarContext);
  const userData = JSON.parse(getLocalStorageItem('userData') || '{}');
  const slug = userData?.slug;

  const population = isB2BFansMode ? 'members' : 'employees';
  const base = `/${slug}/guided-path`;

  const pages = [
    {
      label: 'Overview',
      path: base,
      description: 'Take-up across both kinds, and whether the rituals get done'
    },
    {
      label: 'AI Plan',
      path: `${base}/${PLAN_PATHS.ai}`,
      description: 'Generated from a prompt — take-up, activity and follow-through'
    },
    {
      label: 'Custom Plan',
      path: `${base}/${PLAN_PATHS.custom}`,
      description: 'Built from the questionnaire — take-up, activity and time committed'
    },
    {
      label: isB2BFansMode ? 'Members' : 'Employees',
      path: `${base}/users`,
      description: 'Who is using it and how much — never what their plan says'
    }
  ];

  return (
    <CompanySectionLayout
      title="Guided Path"
      subtitle={`How many of your ${population} are on a 30-day wellbeing plan, and whether they keep going. Take-up and engagement only — no plan name, description or ritual is read at all, and groups too small to anonymise are not reported. There is no completion rate here on purpose; the underlying flag is set by a scheduled job when a plan expires, not when somebody finishes it.`}
      pages={pages}
    >
      {children}
    </CompanySectionLayout>
  );
};

export default CompanyGuidedPathLayout;
